import { PrinterOutlined } from '@ant-design/icons';
import { DrawerCloseButton, PrintSelectionModal, ServiceHistoryCard } from '@components';
import { useView } from '@hooks';
import { useStore } from '@stores';
import { Button, Drawer, Empty, Pagination, Skeleton, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

const PAGE_SIZE = 5;

interface IProps {
  open: boolean;
  vin?: string;
  registrationNumber?: string;
  onClose: () => void;
}

/**
 * The vehicle's complete service history, opened from the customer card on an order. Same cards,
 * paging and print as the /vehicle-histories/:term page, so the two never drift apart.
 */
export const ServiceHistoryDrawer: React.FC<IProps> = observer(
  ({ open, vin, registrationNumber, onClose }) => {
    const { isMobile } = useView();
    const { serviceHistoryStore } = useStore();
    const { searchResults, searchLoading, searchTotalCount } = serviceHistoryStore;
    const [currentPage, setCurrentPage] = useState(1);
    const [printModalOpen, setPrintModalOpen] = useState(false);

    // VIN is the vehicle's identity; the plate is only a fallback for a job booked without one.
    const searchTerm = vin || registrationNumber || '';

    useEffect(() => {
      if (!open || !searchTerm) return undefined;
      setCurrentPage(1);
      serviceHistoryStore.runSearch({ searchTerm, maxResultCount: PAGE_SIZE, skipCount: 0 });
      return () => serviceHistoryStore.resetSearch();
    }, [open, searchTerm, serviceHistoryStore]);

    const handlePageChange = (page: number) => {
      setCurrentPage(page);
      serviceHistoryStore.runSearch({
        searchTerm,
        maxResultCount: PAGE_SIZE,
        skipCount: (page - 1) * PAGE_SIZE,
      });
    };

    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, searchTotalCount);

    return (
      <Drawer
        title="Service history"
        open={open}
        onClose={onClose}
        styles={{ wrapper: { width: isMobile ? '100%' : 720 } }}
        destroyOnHidden
        closable={false}
        extra={
          <>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => setPrintModalOpen(true)}
              disabled={!vin}
              style={{ marginRight: 8 }}
            >
              Print
            </Button>
            <DrawerCloseButton onClick={onClose} />
          </>
        }
      >
        {searchLoading && searchResults.length === 0 ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : searchResults.length === 0 ? (
          <Empty description="No service history for this vehicle yet" />
        ) : (
          searchResults.map((item) => <ServiceHistoryCard key={item.id} item={item} />)
        )}

        {searchTotalCount > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 16,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Showing {startItem} to {endItem} of {searchTotalCount} historical entries
            </Typography.Text>
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={searchTotalCount}
              onChange={handlePageChange}
              showSizeChanger={false}
              size="small"
            />
          </div>
        )}

        {vin && (
          <PrintSelectionModal open={printModalOpen} onClose={() => setPrintModalOpen(false)} vin={vin} />
        )}
      </Drawer>
    );
  }
);
